import { Component, DestroyRef, inject, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { translate } from '@jsverse/transloco';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import {
  ConfigurationService,
  SemantriaConfiguration,
} from 'app/providers/semantria-api/configuration.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

import { ErrorObjectUtils } from 'app/utils/error-object.utils';
import { configNameValidatorFactoryAsync } from '../../configuration-utils';
import { ConfigVisibiltyOptions } from '../../pages/configuration-details/configuration-details.component';

export interface ConfigurationGeneralOptions {
  configName: string;
  configDescription: string;
  showConfigDescription: boolean;
  configTags: string[];
  showConfigTags: boolean;
}
@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-general',
  templateUrl: './configuration-general.component.html',
  styleUrls: ['./configuration-general.component.scss'],
  standalone: false,
})
export class ConfigurationGeneralComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly origConfiguration = input<SemantriaConfiguration>();
  readonly origVisibilityOptions = input<ConfigVisibiltyOptions>();
  readonly isReadOnly = input<boolean>();
  readonly formChange = output<ConfigurationGeneralOptions>();
  readonly validityChange = output<boolean>();
  configGeneralForm: UntypedFormGroup;
  configNameStatus: ErrorObject = { state: EnumErrorStates.SUCCESS };
  configName: string;
  configDescription: string;

  newForm: ConfigurationGeneralOptions = {
    configName: '',
    configDescription: '',
    showConfigDescription: true,
    configTags: [],
    showConfigTags: true,
  };

  private destroyRef = inject(DestroyRef);

  constructor(
    private formBuilder: UntypedFormBuilder,
    private configService: ConfigurationService
  ) {}

  get name() {
    return this.configGeneralForm.get('configName');
  }

  ngOnInit(): void {
    this.configName = this.origConfiguration().name;
    this.configDescription = this.origConfiguration().notes || '';

    this.newForm.configName = this.origConfiguration().name;
    this.newForm.configDescription = this.origConfiguration().notes || '';
    this.newForm.showConfigDescription = this.origVisibilityOptions().showConfigDescription;
    this.origConfiguration().tags
      ? (this.newForm.configTags = [...this.origConfiguration().tags])
      : (this.newForm.configTags = []);
    this.newForm.showConfigTags = this.origVisibilityOptions().showConfigTags;

    this.configGeneralForm = this.formBuilder.group({
      configName: [
        { value: this.configName, disabled: this.isReadOnly() },
        { validators: [Validators.required] },
      ],
      configDescription: [{ value: this.configDescription, disabled: this.isReadOnly() }],
    });
    this.name.setAsyncValidators(
      configNameValidatorFactoryAsync(this.configService, this.origConfiguration(), [
        this.origConfiguration(),
      ])
    );

    this.configGeneralForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.newForm.configName = val.configName.trim();
        this.newForm.configDescription = val.configDescription.trim();
        this.formChange.emit(this.newForm);
      });
    this.configGeneralForm.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        this.configNameStatus = ErrorObjectUtils.create();

        if (status === 'INVALID') {
          const nameError = this.configGeneralForm.controls.configName.errors;

          switch (Object.keys(nameError)[0]) {
            case 'required':
              ErrorObjectUtils.setError(
                this.configNameStatus,
                translate('configuration-general-component.name-error')
              );
              break;
            case 'maxLength':
              ErrorObjectUtils.setError(
                this.configNameStatus,
                translate('configuration-general-component.character-limit-error', {
                  maxLength: nameError.maxLength.maxLength,
                })
              );
              break;
            case 'nameAlreadyExists':
              ErrorObjectUtils.setError(
                this.configNameStatus,
                translate('configuration-general-component.exists-error')
              );
              break;
          }
        }

        this.validityChange.emit(status === 'VALID');
      });
  }

  onToggleShowDescription(showDescription: boolean) {
    this.newForm.showConfigDescription = showDescription;
    this.formChange.emit(this.newForm);
  }

  onToggleShowTags(showTags: boolean) {
    this.newForm.showConfigTags = showTags;
    this.formChange.emit(this.newForm);
  }

  onTagAdded(newTag: string) {
    this.newForm.configTags.push(newTag);
    this.formChange.emit(this.newForm);
  }

  onTagRemoved(index: number) {
    this.newForm.configTags.splice(index, 1);
    this.formChange.emit(this.newForm);
  }
}
